import React, { useState, useRef, useCallback, useMemo } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Checkbox } from 'primereact/checkbox';
import { useTranslation } from 'localization';
import { FeedFilterState, FeedFilterCategory } from 'types/timeline';
import {
  FEED_FILTER_CATEGORIES,
  getDisabledCount,
  isDefaultFilterState,
  resetFilterState,
} from '../../../config/feedFilterConfig';
import './FeedFilterButtonMolecule.scss';

export interface FeedFilterButtonMoleculeProps {
  filters: FeedFilterState;
  onFiltersChange: (filters: FeedFilterState) => void;
  className?: string;
}

type CategoryState = 'all' | 'none' | 'partial';

function getCategoryState(
  category: FeedFilterCategory,
  filters: FeedFilterState
): CategoryState {
  if (filters.disabledCategories.includes(category.id)) return 'none';
  const hasDisabled = category.eventTypes.some(
    (et) =>
      filters.disabledEventTypes.includes(et.value) ||
      (et.subtypes?.some((st) =>
        filters.disabledSubtypes.includes(`${et.value}:${st.id}`)
      ) ?? false)
  );
  return hasDisabled ? 'partial' : 'all';
}

const FeedFilterButtonMolecule: React.FC<FeedFilterButtonMoleculeProps> = ({
  filters,
  onFiltersChange,
  className = '',
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanel>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  const disabledCount = useMemo(() => getDisabledCount(filters), [filters]);
  const isDefault = useMemo(() => isDefaultFilterState(filters), [filters]);

  const handleToggleButton = useCallback((e: React.MouseEvent) => {
    overlayRef.current?.toggle(e);
  }, []);

  const handleToggleExpand = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }, []);

  const handleToggleCategory = useCallback(
    (category: FeedFilterCategory) => {
      const state = getCategoryState(category, filters);
      if (state === 'all') {
        onFiltersChange({
          ...filters,
          disabledCategories: [...filters.disabledCategories, category.id],
        });
      } else {
        const etValues = new Set<string>(category.eventTypes.map((et) => et.value));
        const stKeys = new Set<string>(category.eventTypes.flatMap(
          (et) => et.subtypes?.map((st) => `${et.value}:${st.id}`) ?? []
        ));
        onFiltersChange({
          ...filters,
          disabledCategories: filters.disabledCategories.filter(
            (id) => id !== category.id
          ),
          disabledEventTypes: filters.disabledEventTypes.filter(
            (et) => !etValues.has(et)
          ),
          disabledSubtypes: filters.disabledSubtypes.filter(
            (st) => !stKeys.has(st)
          ),
        });
      }
    },
    [filters, onFiltersChange]
  );

  const handleToggleEventType = useCallback(
    (eventType: string) => {
      const isDisabled = filters.disabledEventTypes.includes(eventType);
      onFiltersChange({
        ...filters,
        disabledEventTypes: isDisabled
          ? filters.disabledEventTypes.filter((et) => et !== eventType)
          : [...filters.disabledEventTypes, eventType],
      });
    },
    [filters, onFiltersChange]
  );

  const handleToggleSubtype = useCallback(
    (subtypeKey: string) => {
      const isDisabled = filters.disabledSubtypes.includes(subtypeKey);
      onFiltersChange({
        ...filters,
        disabledSubtypes: isDisabled
          ? filters.disabledSubtypes.filter((st) => st !== subtypeKey)
          : [...filters.disabledSubtypes, subtypeKey],
      });
    },
    [filters, onFiltersChange]
  );

  const handleToggleConversations = useCallback(() => {
    onFiltersChange({
      ...filters,
      conversationsEnabled: !filters.conversationsEnabled,
    });
  }, [filters, onFiltersChange]);

  const handleReset = useCallback(() => {
    onFiltersChange(resetFilterState());
  }, [onFiltersChange]);

  const triggerClass = `feed-filter-button__trigger${isDefault ? '' : ' feed-filter-button__trigger--active'}`;

  return (
    <div className={`feed-filter-button ${className}`.trim()}>
      <button
        type="button"
        className={triggerClass}
        onClick={handleToggleButton}
        aria-label={t('feed.filters.title')}
        title={t('feed.filters.title')}
      >
        <i className="pi pi-filter" />
        {disabledCount > 0 && (
          <span className="feed-filter-button__badge">{disabledCount}</span>
        )}
      </button>

      <OverlayPanel
        ref={overlayRef}
        className="feed-filter-popover"
        dismissable
      >
        <div className="feed-filter-popover__content">
          <div className="feed-filter-popover__header">
            <span className="feed-filter-popover__title">
              {t('feed.filters.title')}
            </span>
            <button
              type="button"
              className="feed-filter-popover__close"
              onClick={() => overlayRef.current?.hide()}
              aria-label={t('common.close', { defaultValue: 'Close' })}
            >
              <i className="pi pi-times" />
            </button>
          </div>

          {FEED_FILTER_CATEGORIES.map((category) => {
            const state = getCategoryState(category, filters);
            const isExpanded = expandedCategories.has(category.id);
            const isCategoryOff = state === 'none';

            return (
              <div key={category.id} className="feed-filter-popover__category">
                <button
                  type="button"
                  className="feed-filter-popover__category-header"
                  onClick={() => handleToggleExpand(category.id)}
                >
                  <i
                    className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'} feed-filter-popover__expand-icon`}
                  />
                  <i
                    className={`pi ${category.icon} feed-filter-popover__category-icon`}
                  />
                  <span className="feed-filter-popover__category-label">
                    {t(category.labelKey)}
                  </span>
                  <Checkbox
                    checked={state !== 'none'}
                    icon={state === 'partial' ? 'pi pi-minus' : undefined}
                    onChange={() => handleToggleCategory(category)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </button>

                {isExpanded && (
                  <div className="feed-filter-popover__event-types">
                    {category.eventTypes.map((eventType) => {
                      const isEnabled =
                        !isCategoryOff &&
                        !filters.disabledEventTypes.includes(eventType.value);

                      return (
                        <div key={eventType.value}>
                          <label className="feed-filter-popover__event-row">
                            <Checkbox
                              checked={isEnabled}
                              disabled={isCategoryOff}
                              onChange={() =>
                                handleToggleEventType(eventType.value)
                              }
                            />
                            <i
                              className={`pi ${eventType.icon} feed-filter-popover__event-icon`}
                            />
                            <span>{t(eventType.labelKey)}</span>
                          </label>

                          {eventType.subtypes?.map((subtype) => {
                            const subtypeKey = `${eventType.value}:${subtype.id}`;
                            const subtypeEnabled =
                              isEnabled &&
                              !filters.disabledSubtypes.includes(subtypeKey);
                            return (
                              <label
                                key={subtype.id}
                                className="feed-filter-popover__subtype-row"
                              >
                                <Checkbox
                                  checked={subtypeEnabled}
                                  disabled={!isEnabled || isCategoryOff}
                                  onChange={() =>
                                    handleToggleSubtype(subtypeKey)
                                  }
                                />
                                <span>{t(subtype.labelKey)}</span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="feed-filter-popover__divider" />

          <label className="feed-filter-popover__conversations">
            <span className="feed-filter-popover__expand-icon" aria-hidden="true" />
            <i className="pi pi-comments feed-filter-popover__category-icon" />
            <span>{t('feed.filters.conversations')}</span>
            <Checkbox
              checked={filters.conversationsEnabled}
              onChange={handleToggleConversations}
            />
          </label>

          {!isDefault && (
            <>
              <div className="feed-filter-popover__divider" />
              <button
                type="button"
                className="feed-filter-popover__reset"
                onClick={handleReset}
              >
                <i className="pi pi-refresh" />
                <span>{t('feed.filters.reset')}</span>
              </button>
            </>
          )}
        </div>
      </OverlayPanel>
    </div>
  );
};

FeedFilterButtonMolecule.displayName = 'FeedFilterButtonMolecule';

export default FeedFilterButtonMolecule;
